/**
 * Tax-rate (GST) defaults + per-tenant resolver — CLIENT wrapper.
 *
 * Wave 1: the math + label logic moved to the SHARED module
 * shared/src/money/tax.ts (one source of truth for client + server — the
 * functions bundle inlines the same code, ending the hand-mirrored pair).
 * This file keeps the client-only parts: the TenantConfig-shaped resolver
 * and the localStorage-backed sync accessor TenantContext feeds.
 *
 * UNIFIED LABEL BEHAVIOUR (closes KNOWN_ISSUES L-1): taxRateLabel now bands
 * the word like the server always did — GST at ~10%, VAT at 15–25%, Tax
 * otherwise — instead of always "GST". Identical output at the 10% rate
 * every tenant uses today.
 *
 * ── SCOPE ──────────────────────────────────────────────────────────────────
 * Stored money stays ex-GST (Xero line items + Stripe charges depend on it).
 * This helper only powers DISPLAY grossing-up. Never use it to re-store a
 * grossed-up value into a line item / sellPrice / Xero amount.
 */
import type { TenantConfig } from '@/types/tenant.types';
import {
  DEFAULT_TENANT_TAX_RATE,
  buildTaxLabel,
  isValidTaxRate,
  sanitizeGstRate,
} from '@shared/money/tax';

export { DEFAULT_TENANT_TAX_RATE };

/** Resolve with the healing diagnostics the old implementation logged —
 *  a healed (string/percent-form) rate warns so the corrupt source field
 *  gets noticed and fixed, matching pre-Wave-1 behaviour.
 *
 *  Each DISTINCT message warns ONCE per session: this resolver runs on
 *  every money render, and a corrupt tenant field (e.g. defaultTaxRate
 *  stored as the string "0.10") was printing hundreds of identical
 *  lines per page — burying real warnings and costing render time. */
const warnedTaxRateMessages = new Set<string>();
function resolveTaxRate(rate: unknown): number {
  return isValidTaxRate(rate)
    ? rate
    : sanitizeGstRate(rate, DEFAULT_TENANT_TAX_RATE, (message) => {
        if (warnedTaxRateMessages.has(message)) return;
        warnedTaxRateMessages.add(message);
        console.warn(`[taxRate] ${message}`);
      });
}

const TAX_RATE_STORAGE_KEY = 'tenant_tax_rate';

/**
 * Resolve the GST rate (decimal) for a tenant. Missing tenant, missing
 * `business.defaultTaxRate`, or an out-of-range value returns the platform
 * default; malformed-but-recoverable values heal (string '0.1' → 0.1,
 * percent-form 10 → 0.10). Prefer in React components (reactive via
 * useTenant).
 */
export function getTenantTaxRate(
  tenant: TenantConfig | null | undefined,
): number {
  return resolveTaxRate(tenant?.business?.defaultTaxRate);
}

/**
 * Synchronous GST-rate accessor for pure helpers + services without React
 * context — the tax-rate analogue of getCurrencySync()/getLocaleSync(). Reads
 * the value TenantContext caches on load (see cacheTenantTaxRate); falls back
 * to the platform default before the tenant has loaded.
 */
export function getTenantTaxRateSync(): number {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_TENANT_TAX_RATE;
  }
  const raw = window.localStorage.getItem(TAX_RATE_STORAGE_KEY);
  if (raw == null) return DEFAULT_TENANT_TAX_RATE;
  return resolveTaxRate(Number(raw));
}

/**
 * Cache the active tenant's GST rate to localStorage so getTenantTaxRateSync()
 * can read it. Called by TenantContext whenever a tenant config loads/updates.
 */
export function cacheTenantTaxRate(
  tenant: TenantConfig | null | undefined,
): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(
    TAX_RATE_STORAGE_KEY,
    String(getTenantTaxRate(tenant)),
  );
}

/**
 * Human-readable tax label, e.g. "GST (10%)" / "VAT (20%)". Word banded by
 * rate (shared buildTaxLabel), percentage precision-trimmed (0.125 →
 * "12.5%"). Used everywhere a tax line/label is rendered so the wording and
 * percentage track the tenant rate.
 */
export function taxRateLabel(rate: number = getTenantTaxRateSync()): string {
  return buildTaxLabel(isValidTaxRate(rate) ? rate : DEFAULT_TENANT_TAX_RATE);
}
